import { ErrorResponse, Purchase, ServiceResponse, UseCampaignResponse } from '@bzbs/react-api-client';
import React, { createContext, useContext, useRef, useState } from 'react';
import { useBuzzebeesServiceContext } from './buzzebees_service_provider';

type PurchaseContextType = {
  purchases: Purchase[];
  isLoading: boolean;
  error: ErrorResponse | null;
  canLoadMore: boolean;
  actions: {
    fetchPurchases: (
      config: string,
      top: number,
      startDate?: string,
      endDate?: string
    ) => Promise<ServiceResponse<Purchase[]>>;
    use: (redeemKey: string) => Promise<ServiceResponse<UseCampaignResponse>>;
    loadMore: () => Promise<ServiceResponse<Purchase[]>>;
  };
};

const PurchaseContext = createContext({
  purchases: [],
  isLoading: false,
  error: null,
  canLoadMore: false,
  actions: {
    fetchPurchases: async () => ({ type: 'client-error', message: 'Not implemented' }),
    use: async () => ({ type: 'client-error', message: 'Not implemented' }),
    loadMore: async () => ({ type: 'client-error', message: 'Not implemented' }),
  },
} as PurchaseContextType);

export function usePurchaseContext() {
  return useContext(PurchaseContext);
}

export function PurchaseProvider({ children }: { children: React.ReactNode }) {
  const [purchases, setPurchases] = useState<Purchase[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ErrorResponse | null>(null);
  const [canLoadMore, setCanLoadMore] = useState(false);

  const { bzbsService } = useBuzzebeesServiceContext();

  const currentConfig = useRef<string>('purchase');
  const currentTop = useRef<number>(25);
  const currentSkip = useRef<number>(0);
  const currentStartDate = useRef<string>('');
  const currentEndDate = useRef<string>('');

  const fetchPurchases = async (
    config: string,
    top: number,
    startDate?: string,
    endDate?: string
  ): Promise<ServiceResponse<Purchase[]>> => {
    currentConfig.current = config;
    currentTop.current = top;
    currentSkip.current = 0;
    currentStartDate.current = startDate ?? '';
    currentEndDate.current = endDate ?? '';

    setIsLoading(true);
    const response = await bzbsService.historyApi.redeemHistories({
      config: config,
      byConfig: true,
      top: top,
      skip: currentSkip.current,
      startDate: startDate,
      endDate: endDate,
    });
    setIsLoading(false);

    if (response.type === 'success') {
      setPurchases(response.model);
      setError(null);
      if (response.model.length === currentTop.current) {
        setCanLoadMore(true);
      }
    } else {
      setError(response);
    }

    return response;
  };

  const loadMore = async (): Promise<ServiceResponse<Purchase[]>> => {
    currentSkip.current += currentTop.current;

    setIsLoading(true);
    const response = await bzbsService.historyApi.redeemHistories({
      config: currentConfig.current,
      byConfig: true,
      top: currentTop.current,
      skip: currentSkip.current,
      startDate: currentStartDate.current,
      endDate: currentEndDate.current,
    });
    setIsLoading(false);

    if (response.type === 'success') {
      setPurchases([...purchases, ...response.model]);
      setError(null);
      if (response.model.length === currentTop.current) {
        setCanLoadMore(true);
      } else {
        setCanLoadMore(false);
      }
    } else {
      setError(response);
    }

    return response;
  };

  const use = async (redeemKey: string): Promise<ServiceResponse<UseCampaignResponse>> => {
    setIsLoading(true);
    const response = await bzbsService.historyApi.use({ redeemKey: redeemKey });
    setIsLoading(false);

    if (response.type === 'success') {
      setError(null);
    } else {
      setError(response);
    }

    return response;
  };

  return (
    <PurchaseContext.Provider
      value={{
        purchases,
        isLoading,
        error,
        canLoadMore,
        actions: {
          fetchPurchases,
          loadMore,
          use,
        },
      }}
    >
      {children}
    </PurchaseContext.Provider>
  );
}
